// src/pages/loginauth.js
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { defaultRouteForAccess, getUserAccess } from "../utils/authRoles";
import { withTimeout } from "../utils/asyncTimeout";
import { traceAsync } from "../utils/perfTrace";

const AUTH_CALLBACK_TIMEOUT_MS = 25000;

const getSafeRedirect = (url) => {
  const redirect = new URL(url).searchParams.get("redirect");
  if (!redirect || !redirect.startsWith("/") || redirect.startsWith("//")) return "";
  return redirect;
};

export default function LoginAuth() {
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const url = window.location.href;
        const redirectTo = getSafeRedirect(url);

        const parsedUrl = new URL(url);
        const code = parsedUrl.searchParams.get("code");
        const hashParams = new URLSearchParams(parsedUrl.hash.replace(/^#/, ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (code) {
          const { error } = await traceAsync(
            "callback.exchangeCodeForSession",
            () => withTimeout(
              supabase.auth.exchangeCodeForSession(code),
              AUTH_CALLBACK_TIMEOUT_MS,
              "Auth callback timed out"
            ),
            {}
          );
          if (error) {
            console.error("[AuthCallback] exchangeCodeForSession:", error);
          }
        } else if (accessToken && refreshToken) {
          const { error } = await traceAsync(
            "callback.setSession",
            () => withTimeout(
              supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              }),
              AUTH_CALLBACK_TIMEOUT_MS,
              "Auth callback timed out"
            ),
            {}
          );
          if (error) {
            console.error("[AuthCallback] setSession:", error);
          }
        }

        // Check if we have a session and route accordingly
        const { data } = await traceAsync(
          "callback.getSession",
          () => withTimeout(
            supabase.auth.getSession(),
            AUTH_CALLBACK_TIMEOUT_MS,
            "Auth callback session check timed out"
          ),
          {}
        );

        if (!mounted) return;

        if (data?.session) {
          const access = await traceAsync("callback.getUserAccess", () => getUserAccess(), {});
          const target = redirectTo || defaultRouteForAccess(access);
          navigate(target, { replace: true });
        } else {
          // Fallback: no session; send them to login with a tiny banner
          navigate("/login?confirmed=1", { replace: true });
        }
      } catch (e) {
        console.error("[AuthCallback] unexpected error:", e);
        navigate("/login?confirmed=1", { replace: true });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-gray-600">
      Finalizing sign-in…
    </div>
  );
}
